import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Container,
  Row,
  Col,
  ListGroup,
  ListGroupItem,
  Card,
  CardBody,
  Spinner
} from 'reactstrap';
import PropTypes from 'prop-types';
import { checkLocationEligibility } from '../../axios/locations/index.api';
import { useAuthStore } from '../../zustand-store/auth.store';
import { useNavigate } from 'react-router-dom';

const initialSteps = [
  { label: 'Welcome', key: 'intro', iconClass: 'fas fa-info-circle', completed: true },
  { label: 'Service', key: 'service', iconClass: 'fas fa-cogs', completed: false },
  {
    label: 'Payment Integration',
    key: 'paymentAccount',
    iconClass: 'fas fa-credit-card',
    completed: false
  },
  { label: 'Business Logo', key: 'businessLogo', iconClass: 'fas fa-image', completed: false },
  {
    label: 'Location Activation',
    key: 'locationActivation',
    iconClass: 'fas fa-map-marker-alt',
    completed: false
  }
];

const GuidedSetupWizardModal = ({ isOpen, toggle }) => {
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [introCompleted, setIntroCompleted] = useState(() => {
    return localStorage.getItem('introCompleted') === 'true';
  });

  const locationId = useAuthStore((state) => state.locationId);
  const businessId = useAuthStore((state) => state.businessId);
  const wizardLoader = useAuthStore((state) => state.wizardLoader);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && locationId) {
      setLoading(true);
      fetchStepStatus();
    }
  }, [isOpen, locationId]);

  const fetchStepStatus = async () => {
    try {
      const res = await checkLocationEligibility(locationId);
      const eligibilityData = res?.data?.data || {};

      const updatedSteps = initialSteps.map((step) => ({
        ...step,
        completed: step.key === 'intro' ? true : !!eligibilityData[step.key]
      }));

      const allCompleted = updatedSteps
        .filter((step) => step.key !== 'intro')
        .every((step) => step.completed);

      if (allCompleted) {
        setSteps([
          {
            label: 'Setup Complete',
            key: 'success',
            iconClass: 'fas fa-check-circle',
            completed: true,
            content: (
              <div className="text-center">
                <h4 className="text-success">Your Setup is complete.</h4>
                <p>
                  Now you can update the appointment hours, breaks, time off, additional content and
                  information on the appointment page for your visitors and more.
                </p>
              </div>
            )
          }
        ]);
        setStepIndex(0);
      } else {
        const enrichedSteps = updatedSteps.map((step) => {
          let content = null;
          let onContinue = null;

          switch (step.key) {
            case 'intro':
              content = (
                <div>
                  <h4>Welcome to the Setup Guide!</h4>
                  <p>
                    This wizard will guide you through setting up your business on the platform.
                  </p>
                  <p>
                    Click <strong>Continue</strong> to get started.
                  </p>
                </div>
              );
              onContinue = (done) => {
                localStorage.setItem('introCompleted', 'true');
                setIntroCompleted(true);
                setStepIndex(1);
                done();
              };
              break;

            case 'service':
              content = (
                <ul>
                  <li>When you click Continue, you will be redirected to a New Service form.</li>
                  <li>
                    {`
                  Fill the necessary information on the Service. Your customer's will be booking
appointments for this service.`}
                  </li>
                  <li>
                    Once you click Save on the Service popup, you will be redirected back to this
                    current page to complete the Setup.
                  </li>
                </ul>
              );
              onContinue = (done) => {
                navigate('/services');
                setTransitioning(true);
                useAuthStore.getState().setUpwizardLoader();
                setTimeout(() => {
                  setTransitioning(true);
                  document.querySelector('#open-add-service-modal')?.click();
                  done();
                  useAuthStore.getState().appTourToggle();
                  setTimeout(() => {
                    document.querySelector('#add-service').innerText =
                      'Add Service and Continue Setup';
                  }, 1000);
                  useAuthStore.getState().setUpwizardLoader();
                }, 2000);
              };
              break;

            case 'paymentAccount':
              content = (
                <ul>
                  <li>When you click Continue, you will be redirected to the integrations page.</li>
                  <li>Click on SquarePay Integration.</li>
                  <li>
                    Login to your SquarePay account and select one of the locations if there are
                    multiple locations.
                  </li>
                  <li>
                    Once you click Save, you will be redirected to this current page to complete the
                    Setup.
                  </li>
                </ul>
              );
              onContinue = (done) => {
                navigate('/integration-settings');
                useAuthStore.getState().setUpwizardLoader();
                setTimeout(() => {
                  document.querySelector('#square-integration-card')?.click();
                  done();
                  useAuthStore.getState().appTourToggle();
                  useAuthStore.getState().setUpwizardLoader();
                }, 1000);
              };
              break;

            case 'businessLogo':
              content = (
                <ul>
                  <li>
                    When you click Continue, you will be redirected to the Business information
                    popup form.
                  </li>
                  <li>
                    Once on that page, upload a logo file from your computer or phone to Seamless
                    Slot. The logo dimensions are listed on the form.
                  </li>
                  <li>
                    Once uploaded, click Save. You will be redirected to this current page to
                    complete the Setup.
                  </li>
                </ul>
              );
              onContinue = (done) => {
                navigate('/business');
                useAuthStore.getState().setUpwizardLoader();
                setTimeout(() => {
                  document
                    .querySelector(
                      '#layout-wrapper > div.main-content > div.page-content > div > div.bg-white.pt-4.row > div.table-responsive > table > tbody > tr:nth-child(1) > td:nth-child(7) > div > div > div > button:nth-child(1)'
                    )
                    ?.click();
                  done();
                  useAuthStore.getState().appTourToggle();
                  useAuthStore.getState().setUpwizardLoader();
                }, 2000);
              };
              break;

            case 'locationActivation':
              content = (
                <ul>
                  <li>
                    {`When you click Continue, you will be redirected to your Business’s Locations Page.`}
                  </li>
                  <li>On the right side of your location listing, click on the Edit button.</li>
                  <li>A popup form will appear.</li>
                  <li>Scroll down to the bottom of the form.</li>
                  <li>Activate the Location.</li>
                  <li>Click continue.</li>
                </ul>
              );
              onContinue = (done) => {
                navigate(`/business/${businessId}`);
                useAuthStore.getState().setUpwizardLoader();
                setTimeout(() => {
                  document
                    .querySelector(
                      '#layout-wrapper > div.main-content > div.page-content > div > div:nth-child(2) > div > div > div > div.d-flex.align-items-center.gap-3.pointer > div:nth-child(2)'
                    )
                    ?.click();
                  setTimeout(() => {
                    document
                      .querySelector(
                        '#layout-wrapper > div.main-content > div.page-content > div > div:nth-child(2) > div > div > div > div.mt-3 > div > div.table-responsive > table > tbody > tr:nth-child(1) > td:nth-child(6) > div > div > div > button:nth-child(1)'
                      )
                      ?.click();
                    const modal = document.querySelector(
                      'body > div:nth-child(3) > div > div.modal.fade.show'
                    );
                    if (modal) modal.scrollTop = modal.scrollHeight;

                    useAuthStore.getState().appTourToggle();
                    done();
                    useAuthStore.getState().setUpwizardLoader();
                  }, 2000);
                }, 2000);
              };
              break;

            default:
              content = null;
          }

          return { ...step, content, onContinue };
        });

        const filteredSteps = introCompleted
          ? enrichedSteps.filter((s) => s.key !== 'intro')
          : enrichedSteps;

        const firstIncomplete = filteredSteps.findIndex((step) => !step.completed);
        setSteps(filteredSteps);
        setStepIndex(firstIncomplete !== -1 ? firstIncomplete : 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    const currentStep = steps[stepIndex];
    if (currentStep?.onContinue) {
      setTransitioning(true);
      currentStep.onContinue(() => {
        setTransitioning(false);
        if (stepIndex < steps.length - 1) {
          setStepIndex(stepIndex + 1);
        }
      });
    } else {
      if (stepIndex < steps.length - 1) {
        setStepIndex(stepIndex + 1);
      }
    }
  };

  const closeModal = () => {
    toggle();
    setStepIndex(0);
  };

  const isOnlySuccessStep = steps.length === 1 && steps[0].key === 'success';

  return (
    <Modal isOpen={isOpen} toggle={closeModal} size="xl" backdrop="static">
      <ModalHeader toggle={closeModal}>Setup Guide</ModalHeader>
      <ModalBody>
        {loading || transitioning || wizardLoader ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ height: '300px' }}>
            <Spinner color="primary" />
            <p className="mt-3">Please wait while we take you to the right place...</p>
          </div>
        ) : (
          <Container fluid>
            <Row>
              {!steps.filter((step) => step.key !== 'intro').every((step) => step.completed) && (
                <Col md="3" className="border-end bg-light p-0">
                  <h6 className="p-3 border-bottom">Steps</h6>
                  <ListGroup flush>
                    {steps.map(
                      (step, idx) =>
                        step.key !== 'intro' &&
                        step.key !== 'success' && (
                          <ListGroupItem
                            key={idx}
                            className={`d-flex align-items-center border-0 ${idx === stepIndex ? 'bg-primary text-white fw-bold' : ''}`}>
                            <i className={`${step.iconClass} me-2`}></i>
                            {step.label}
                            {step.completed && (
                              <i className="ms-2 text-success fas fa-check-circle" />
                            )}
                          </ListGroupItem>
                        )
                    )}
                  </ListGroup>
                </Col>
              )}
              <Col
                md={
                  steps.filter((step) => step.key !== 'intro').every((step) => step.completed)
                    ? '12'
                    : '9'
                }
                className="p-4">
                <Card className="shadow-sm">
                  <CardBody>{steps[stepIndex]?.content}</CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        )}
      </ModalBody>

      {!loading && !transitioning && !isOnlySuccessStep && (
        <ModalFooter className="d-flex justify-content-end">
          <Button color="primary" onClick={nextStep}>
            Continue
          </Button>
        </ModalFooter>
      )}

      {!loading && !transitioning && isOnlySuccessStep && (
        <ModalFooter>
          <Button color="success" onClick={closeModal}>
            Done
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

GuidedSetupWizardModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func
};

export default GuidedSetupWizardModal;
